import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { projectAPI, clientAPI, userAPI } from '../services/api';
import Layout from '../components/Layout';
import DocumentUpload from '../components/DocumentUpload';
import { toast } from 'sonner';
import './AddProject.css';

const Icon = ({ name, size = 18 }) => {
  const icons = {
    arrowLeft: <path d="M19 12H5M12 19l-7-7 7-7" />,
    save: <><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>,
    info: <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></>,
    check: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="M16 18l2-2 4 4" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clients, setClients] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    category: '',
    technology: '',
    repositoryLink: '',
    description: '',
    estimatedHours: '',
    startDate: '',
    endDate: '',
    budget: '',
    projectManager: '',
    teamMembers: [],
    status: 'Planning',
    priority: 'Medium'
  });

  const projectCategories = [
    'New Software Development',
    'Customization / Enhancement',
    'Bug Fixing & Maintenance',
    'UI/UX Redesign',
    'Integration Work',
    'Testing / QA Projects',
    'R&D / Analysis Work'
  ];

  const projectStatuses = ['Planning', 'Active', 'On Hold', 'Completed', 'Cancelled'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  useEffect(() => {
    loadProjectData();
    loadClients();
    loadTeamUsers();
  }, [id]);

  const loadProjectData = async () => {
    try {
      const response = await projectAPI.getById(id);
      const project = response.data.data;
      setFormData({
        name: project.name || '',
        client: project.client?._id || '',
        category: project.category || '',
        technology: project.technology || '',
        repositoryLink: project.gitRepository || '',
        description: project.description || '',
        estimatedHours: project.estimatedHours || '',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        budget: project.budget || '',
        projectManager: project.projectManager?._id || '',
        teamMembers: project.teamMembers?.map(m => m._id) || [],
        status: project.status || 'Planning',
        priority: project.priority || 'Medium'
      });
    } catch (error) {
      toast.error('Failed to load project data');
      navigate('/projects');
    } finally {
      setLoadingData(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientAPI.getAll({ isActive: true });
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadTeamUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setTeamUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load team:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await projectAPI.update(id, formData);
      toast.success('Project updated successfully');
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="add-project-page">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner-new"></div>
            <p>Loading project data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="add-project-page">
        <div className="add-project-header">
          <button className="btn-back" onClick={() => navigate('/projects')}>
            <Icon name="arrowLeft" size={18} />
            Back to Projects
          </button>
          <h1 className="page-title">Edit Project</h1>
          <p className="page-subtitle">Update project details below</p>
        </div>

        <form onSubmit={handleSubmit} className="add-project-form">
          <div className="form-section">
            <div className="section-header">
              <Icon name="info" size={18} />
              <h2 className="section-title">Basic Information</h2>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Project Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter project name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Client <span className="required">*</span></label>
                <select
                  name="client"
                  value={formData.client}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client._id} value={client._id}>{client.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Icon name="check" size={18} />
              <h2 className="section-title">Project Details</h2>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Category <span className="required">*</span></label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {projectCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Project Manager <span className="required">*</span></label>
                <select
                  name="projectManager"
                  value={formData.projectManager}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select PM</option>
                  {teamUsers.filter(u => u.role === 'Project Manager').map(pm => (
                    <option key={pm._id} value={pm._id}>{pm.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  {projectStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  {priorities.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Icon name="search" size={18} />
              <h2 className="section-title">Technical Details</h2>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Technology Stack</label>
                <input
                  type="text"
                  name="technology"
                  value={formData.technology}
                  onChange={handleInputChange}
                  placeholder="React, Node.js, MongoDB"
                />
              </div>

              <div className="form-group">
                <label>Repository Link</label>
                <input
                  type="url"
                  name="repositoryLink"
                  value={formData.repositoryLink}
                  onChange={handleInputChange}
                  placeholder="https://github.com/..."
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Describe the project objectives and scope..."
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Icon name="check" size={18} />
              <h2 className="section-title">Team Members</h2>
            </div>
            <div className="form-group full-width">
              <label>Assign Team Members</label>
              <select
                multiple
                name="teamMembers"
                value={formData.teamMembers}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setFormData({ ...formData, teamMembers: selected });
                }}
                style={{ minHeight: '150px' }}
              >
                {teamUsers.filter(u => ['Developer', 'QA Engineer', 'UI Engineer', 'Project Lead'].includes(u.role)).map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name} - {user.role}
                  </option>
                ))}
              </select>
              <small style={{ color: '#64748b', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                Hold Ctrl (Windows) or Cmd (Mac) to select multiple members
              </small>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Icon name="calendar" size={18} />
              <h2 className="section-title">Timeline & Budget</h2>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Estimated Hours</label>
                <input
                  type="number"
                  name="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Budget</label>
                <div className="amount-input-wrapper">
                  <span className="currency-prefix">
                    {clients.find(c => c._id === formData.client)?.currency || 'INR'}
                  </span>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    style={{ paddingLeft: '50px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Icon name="folder" size={18} />
              <h2 className="section-title">Documents</h2>
            </div>
            <div className="form-group full-width">
              <label>Project Documents</label>
              <DocumentUpload
                entityType="project"
                entityId={id}
                onUploadComplete={(docs) => console.log('Project docs uploaded', docs)}
              // In a real app, you would pass existing documents here
              // existingDocuments={project?.documents || []} 
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate('/projects')} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              <Icon name="save" size={16} />
              {loading ? 'Updating...' : 'Update Project'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditProject;
